import * as React from "react"
import { useEffect, useState, useCallback } from "react"
import {
    Paper,
    Box,
    Avatar,
    Chip,
    Stack,
    Typography,
    Tooltip,
    Skeleton,
    Alert,
} from "@mui/material"
import HomeIcon from "@mui/icons-material/Home"
import ApartmentIcon from "@mui/icons-material/Apartment"


// ---------------------------------------------------------------------------
// DPE colour scale (A = best, G = worst)
//
// DECISION: duplicated from PropertyInfoCard rather than shared, matching
// this codebase's existing convention of small per-file helpers (see
// formatDate/formatAmount repeated across cards) over a premature shared
// utils module.
// ---------------------------------------------------------------------------
const dpeColors = {
    A: "#319530", B: "#32c334", C: "#c5f433", D: "#f3f40b",
    E: "#f4c409", F: "#f49533", G: "#f40a04",
}

function dpeTextColor(hex) {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return lum > 0.55 ? "#000" : "#fff"
}

function kindIcon(kind) {
    return (kind ?? "").toLowerCase().includes("appart") ? <ApartmentIcon /> : <HomeIcon />
}


// ---------------------------------------------------------------------------
// PropertyHeader — persistent identity strip for the property page.
//
// DECISION: this is the one piece of the page that never moves, never hides
// behind a tab, and never scrolls out of view: which property is this,
// where is it, what grade is it, who owns it. Everything else (the current
// lease, the history, the paperwork) is either time-sensitive ("Location")
// or reference detail ("Bien") and lives in the tabs below it. The header
// stays a single row on purpose, so it orients without competing with the
// tabs for vertical space.
//
// Props
//   supabase    – initialised Supabase client
//   propertyId  – UUID of the property to display
// ---------------------------------------------------------------------------
export default function PropertyHeader({ supabase, propertyId }) {

    const [property, setProperty] = useState(null)
    const [dpe,      setDpe]      = useState(null)
    const [owners,   setOwners]   = useState([])
    const [error,    setError]    = useState(null)
    const [loading,  setLoading]  = useState(true)

    const fetchAll = useCallback(async () => {
        setLoading(true)
        setError(null)

        const [propertyRes, dpeRes, ownersRes] = await Promise.all([
            supabase.from("property").select("*").eq("id", propertyId).single(),
            supabase
                .from("dpe")
                .select("energy_consumption_category, valid_until")
                .eq("property_id", propertyId)
                .order("valid_until", { ascending: false })
                .limit(1)
                .maybeSingle(),
            supabase
                .from("property_owner")
                .select(`
                    shares,
                    person:owner_person_id ( first_name, last_name ),
                    company:owner_company_id ( name )
                `)
                .eq("property_id", propertyId),
        ])

        if (propertyRes.error) {
            setError(`Propriété introuvable : ${propertyRes.error.message}`)
            setLoading(false)
            return
        }

        setProperty(propertyRes.data)
        setDpe(dpeRes.data ?? null)
        setOwners((ownersRes.data ?? []).map(o => o.person
            ? `${o.person.first_name} ${o.person.last_name}`
            : (o.company?.name ?? "Inconnu")))

        setLoading(false)
    }, [supabase, propertyId])

    useEffect(() => {
        if (!propertyId) return
        fetchAll()

        const channel = supabase
            .channel(`property-header-${propertyId}`)
            .on("postgres_changes", { event: "*", schema: "public", table: "property", filter: `id=eq.${propertyId}` }, fetchAll)
            .on("postgres_changes", { event: "*", schema: "public", table: "dpe", filter: `property_id=eq.${propertyId}` }, fetchAll)
            .on("postgres_changes", { event: "*", schema: "public", table: "property_owner", filter: `property_id=eq.${propertyId}` }, fetchAll)
            .subscribe()

        return () => { supabase.removeChannel(channel) }
    }, [supabase, propertyId, fetchAll])

    if (error) {
        return <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
    }

    const addressLine = property
        ? [property.address_1, [property.zip, property.city].filter(Boolean).join("\u2009")]
            .filter(Boolean)
            .join(", ")
        : null

    const dpeCat = dpe?.energy_consumption_category
    const dpeBg  = dpeCat ? (dpeColors[dpeCat] ?? "#ccc") : null

    return (
        <Paper
            variant="outlined"
            sx={{ p: 2, mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
        >
            {loading ? (
                <Skeleton variant="circular" width={52} height={52} />
            ) : (
                <Avatar sx={{ width: 52, height: 52, bgcolor: "action.selected", color: "text.secondary" }}>
                    {kindIcon(property?.kind)}
                </Avatar>
            )}

            <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                {loading ? (
                    <>
                        <Skeleton width="35%" sx={{ fontSize: "1.5rem" }} />
                        <Skeleton width="55%" />
                    </>
                ) : (
                    <>
                        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {property?.short_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {addressLine || "—"}
                        </Typography>
                    </>
                )}
            </Box>

            {!loading && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {property?.kind && (
                        <Chip label={property.kind} size="small" variant="outlined" />
                    )}
                    {dpeCat && (
                        <Chip
                            label={`DPE ${dpeCat}`}
                            size="small"
                            sx={{ bgcolor: dpeBg, color: dpeTextColor(dpeBg), fontWeight: 700 }}
                        />
                    )}
                    {owners.length > 0 && (
                        <Tooltip title={owners.join(", ")}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                            >
                                {owners.join(", ")}
                            </Typography>
                        </Tooltip>
                    )}
                </Stack>
            )}
        </Paper>
    )
}